import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch, useKeyboardNavigation } from '@/hooks/useSearch';
import SearchAutocomplete from './SearchAutocomplete';
import type { SearchResult } from '@/types';
import styles from './SearchBar.module.css';

interface SearchBarProps {
  onSelect?: () => void;
  placeholder?: string;
  className?: string;
}

export default function SearchBar({
  onSelect,
  placeholder = 'Search recipes or authors...',
  className = '',
}: SearchBarProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const { query, setQuery, results, isLoading, clearSearch, hasResults } = useSearch();

  const handleSelect = (result: SearchResult) => {
    navigate(`/app/recipe/${result.id}`);
    clearSearch();
    setIsFocused(false);
    inputRef.current?.blur();
    onSelect?.();
  };

  const { selectedIndex, setSelectedIndex } = useKeyboardNavigation(
    results,
    handleSelect,
    isFocused && hasResults
  );

  const showDropdown = isFocused && query.length >= 2;

  return (
    <div className={`${styles.container} ${className}`}>
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          // Delay to allow click on dropdown item
          setTimeout(() => setIsFocused(false), 150);
        }}
      />
      {query && (
        <button
          className={styles.clearButton}
          onClick={() => {
            clearSearch();
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
        >
          ×
        </button>
      )}
      {showDropdown && (
        <SearchAutocomplete
          results={results}
          isLoading={isLoading}
          selectedIndex={selectedIndex}
          onSelect={handleSelect}
          onHover={setSelectedIndex}
          query={query}
        />
      )}
    </div>
  );
}
